import { createIntl, createIntlCache } from "@formatjs/intl";
import {
  asyncWithLDProvider,
  useFlags,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import React, {
  ComponentType,
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import { BrowserRouter, Route } from "react-router-dom";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license";

import { CssBaseline } from "veneer/Theme";
import ThemeProvider from "veneer/ThemeProvider";

import { IntlProvider } from "vistar/i18n/ui";

import Main from "cortex/ui/src/fleet/controller/Main";
import {
  appReducers,
  appSagas,
  pageReducers,
  pageSagas,
} from "cortex/ui/src/fleet/redux-store";
import {
  featureFlagsSyncedAction,
  normalizeFeatureFlags,
  setFeatureFlags,
} from "cortex/ui/src/integrations/featureFlags";
import { LaunchDarklyAuthSyncProvider } from "cortex/ui/src/integrations/LaunchDarklyAsync";
import { init as sentryInit } from "cortex/ui/src/integrations/sentry";
import { NavBarProvider } from "cortex/ui/src/navigation/NavBarProvider";
import { buildApplicationStore } from "cortex/ui/src/redux-store";
import { getRuntimeConfig } from "cortex/ui/src/runtime/config";
import { configureAxiosLoader } from "cortex/ui/src/util/xhr";

// @ts-ignore
import "../../style/index.less";

const storeConfig = {
  appReducers,
  appSagas,
  pageReducers,
  pageSagas,
  debug: true,
};

const store = buildApplicationStore(storeConfig);
configureAxiosLoader(store);

const intlCache = createIntlCache();
const intl = createIntl(
  {
    locale: "en-US",
  },
  intlCache,
);

async function initFleet() {
  sentryInit();

  const cfg = getRuntimeConfig();
  const MUI_X_LICENSE_KEY =
    typeof cfg?.MUI_X_LICENSE_KEY === "string" ? cfg.MUI_X_LICENSE_KEY : "";
  LicenseInfo.setLicenseKey(MUI_X_LICENSE_KEY);

  const launchDarklyClientId = cfg?.LAUNCH_DARKLY_CLIENT_ID as
    | string
    | undefined;
  let LDProvider: ComponentType<{ children: ReactNode }> = Fragment;

  if (launchDarklyClientId) {
    LDProvider = await asyncWithLDProvider({
      clientSideID: launchDarklyClientId,
      context: {
        kind: "user",
        key: "anonymous",
        anonymous: true,
      },
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
      timeout: 5,
    });
  }

  return render(
    <LDProvider>
      <FeatureFlagRuntimeSync />
      <LaunchDarklyShutdownHandler />
      <IntlProvider currency="USD" intl={intl}>
        <Provider store={store}>
          <LaunchDarklyAuthSyncProvider>
            <ThemeProvider>
              <CssBaseline />
              <LocalizationProvider
                adapterLocale="en-US"
                dateAdapter={AdapterMoment}
              >
                <BrowserRouter>
                  <NavBarProvider>
                    <Route component={Main} path="/" />
                  </NavBarProvider>
                </BrowserRouter>
              </LocalizationProvider>
            </ThemeProvider>
          </LaunchDarklyAuthSyncProvider>
        </Provider>
      </IntlProvider>
    </LDProvider>,
    document.getElementById("cortex-fleet"),
  );
}

function FeatureFlagRuntimeSync() {
  const ldFlags = useFlags() as Record<string, unknown>;
  const featureFlags = useMemo(() => normalizeFeatureFlags(ldFlags), [ldFlags]);

  useEffect(() => {
    setFeatureFlags(featureFlags);
    store.dispatch(featureFlagsSyncedAction);
  }, [featureFlags]);

  return null;
}

function LaunchDarklyShutdownHandler() {
  const ldClient = useLDClient();

  useEffect(() => {
    const handleBeforeUnload = () => {
      ldClient?.close();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      ldClient?.close();
    };
  }, [ldClient]);

  return null;
}

export default initFleet();
export { initFleet };
